import { useQuery } from '@tanstack/react-query';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from './useAuth';

interface OnboardingStatus {
  isComplete: boolean;
  flowId: string | null;
  status: string | null;
  lastActivityAt: string | null;
  isExempt: boolean; // True for admins OR users invited to existing organizations
  exemptReason?: 'admin' | 'invited_user' | null;
}

/**
 * Hook to check the current user's onboarding completion status
 * Used for gating access to protected routes
 * 
 * EXEMPT USERS (no onboarding required):
 * - Super admins and platform admins
 * - Users invited to an existing organization (via portal user management)
 */
export const useOnboardingStatus = () => {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['onboarding-status', user?.id],
    queryFn: async (): Promise<OnboardingStatus> => {
      if (!user?.id) {
        return {
          isComplete: false,
          flowId: null,
          status: null,
          lastActivityAt: null,
          isExempt: false,
          exemptReason: null,
        };
      }

      // Check if user has super_admin or platform_admin role
      // These users are EXEMPT from onboarding requirements
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['super_admin', 'platform_admin'])
        .maybeSingle();

      // If user has admin role, they are exempt from onboarding
      if (!roleError && roleData) {
        return {
          isComplete: true, // Treated as complete for routing purposes
          flowId: null,
          status: 'exempt',
          lastActivityAt: null,
          isExempt: true,
          exemptReason: 'admin',
        };
      }

      // Check if user was invited to an organization (invited_by is set in profile)
      // These users are joining an EXISTING organization and don't need onboarding
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('invited_by, client_id')
        .eq('id', user.id)
        .maybeSingle();

      if (!profileError && profileData?.invited_by && profileData?.client_id) {
        // User was invited to an existing organization - exempt from onboarding
        return {
          isComplete: true, // Treated as complete for routing purposes
          flowId: null,
          status: 'exempt',
          lastActivityAt: null,
          isExempt: true,
          exemptReason: 'invited_user',
        };
      }

      // For non-admin/non-invited users, query onboarding_flows to find the user's flow
      const { data: flows, error: flowError } = await supabase
        .from('onboarding_flows')
        .select('id, status, last_activity_at, completed_at')
        .eq('primary_email', user.email || '')
        .order('created_at', { ascending: false })
        .limit(1);

      if (flowError) {
        console.error('Error fetching onboarding status:', flowError);
        throw flowError;
      }

      // If no flow exists, user hasn't started onboarding
      if (!flows || flows.length === 0) {
        return {
          isComplete: false,
          flowId: null,
          status: null,
          lastActivityAt: null,
          isExempt: false,
          exemptReason: null,
        };
      }

      const flow = flows[0];

      // Check if onboarding is complete
      const isComplete = flow.status === 'completed' && !!flow.completed_at;

      return {
        isComplete,
        flowId: flow.id,
        status: flow.status,
        lastActivityAt: flow.last_activity_at,
        isExempt: false,
        exemptReason: null,
      };
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  return {
    isComplete: data?.isComplete ?? false,
    flowId: data?.flowId ?? null,
    status: data?.status ?? null,
    lastActivityAt: data?.lastActivityAt ?? null,
    isExempt: data?.isExempt ?? false,
    exemptReason: data?.exemptReason ?? null,
    isLoading,
    error,
  };
};
